import { h, render } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import type { WidgetConfig } from './config';
import { applyRetentionOffer, prepareRetentionOffer } from './api';

type OfferState = {
  title: string;
  bodyHtml: string;
  couponCode: string;
  discountLabel: string;
  expiresAt: string;
  cta: string;
  error: string;
  loading: boolean;
  applying: boolean;
};

function RetentionOfferPanel(props: { config: WidgetConfig; token: string }) {
  const [state, setState] = useState<OfferState>({
    title: '',
    bodyHtml: '',
    couponCode: '',
    discountLabel: '',
    expiresAt: '',
    cta: 'catalog',
    error: '',
    loading: true,
    applying: false,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await prepareRetentionOffer(props.config, props.token);
      if (cancelled) return;
      if (res.status !== 200 || !res.result) {
        setState((s) => ({ ...s, loading: false, error: 'Offer not found or expired.' }));
        return;
      }
      const offer = res.result.offer || {};
      setState({
        title: String(offer.title || 'Your offer'),
        bodyHtml: String(offer.body_html || ''),
        couponCode: String(res.result.coupon_code || offer.coupon_code || ''),
        discountLabel: String(offer.discount_label || ''),
        expiresAt: String(res.result.expires_at || offer.expires_at || ''),
        cta: String(res.result.cta || offer.cta || 'catalog'),
        error: '',
        loading: false,
        applying: false,
      });
    })().catch(() => {
      if (!cancelled) setState((s) => ({ ...s, loading: false, error: 'Offer not found or expired.' }));
    });
    return () => {
      cancelled = true;
    };
  }, [props.config, props.token]);

  const onApply = async () => {
    setState((s) => ({ ...s, applying: true, error: '' }));
    const res = await applyRetentionOffer(props.config, props.token);
    if (res.status !== 200) {
      setState((s) => ({ ...s, applying: false, error: 'Could not apply coupon.' }));
      return;
    }
    const cta = String(res.result?.cta || state.cta || 'catalog');
    window.location.assign(cta === 'cart' ? '/cart' : '/');
  };

  if (state.loading) {
    return h('div', { class: 'sl-retention-offer', style: 'padding:24px;max-width:640px;margin:24px auto;' }, 'Loading offer…');
  }
  if (state.error && !state.title) {
    return h('div', { class: 'sl-retention-offer', style: 'padding:24px;max-width:640px;margin:24px auto;' }, state.error);
  }

  return h(
    'div',
    { class: 'sl-retention-offer', style: 'padding:24px;max-width:640px;margin:24px auto;' },
    h('h1', { style: 'font-size:1.5rem;margin-bottom:12px;' }, state.title),
    state.bodyHtml ? h('div', { dangerouslySetInnerHTML: { __html: state.bodyHtml } }) : null,
    state.couponCode
      ? h(
          'p',
          { style: 'margin-top:16px;' },
          'Coupon: ',
          h('code', null, state.couponCode),
          state.discountLabel ? ` (${state.discountLabel})` : '',
        )
      : null,
    state.expiresAt ? h('p', { style: 'color:#666;font-size:0.9rem;' }, `Valid until ${state.expiresAt}`) : null,
    state.error ? h('p', { style: 'color:#b91c1c;' }, state.error) : null,
    h(
      'div',
      { style: 'margin-top:20px;display:flex;gap:12px;flex-wrap:wrap;' },
      h(
        'button',
        {
          type: 'button',
          disabled: state.applying,
          onClick: onApply,
          style: 'background:#2563eb;color:#fff;border:0;padding:12px 20px;border-radius:8px;cursor:pointer;',
        },
        state.applying ? 'Applying…' : 'Apply offer',
      ),
      h('a', { href: '/', style: 'padding:12px 20px;' }, 'Continue shopping'),
    ),
  );
}

export function mountRetentionOfferIfNeeded(config: WidgetConfig): void {
  const token = (config.retentionOfferToken || '').trim();
  if (!token || typeof document === 'undefined') return;
  let host = document.getElementById('sl-retention-offer-root');
  if (!host) {
    host = document.createElement('div');
    host.id = 'sl-retention-offer-root';
    const main = document.querySelector('main') || document.body;
    main.prepend(host);
  }
  render(h(RetentionOfferPanel, { config, token }), host);
}
